<template>
  <div class="download-area">
    <p class="download-lead">试用请下载标准版，体积更小。Plus 版仅在需要 AI 分类时选择。</p>

    <div class="package-list" role="radiogroup" aria-label="选择安装包版本">
      <button
        v-for="item in packages"
        :key="item.id"
        type="button"
        class="package-card"
        :class="{ 'is-selected': selectedId === item.id, 'is-recommended': item.recommended }"
        @click="selectedId = item.id"
      >
        <span class="package-card__check" aria-hidden="true"></span>
        <span class="package-card__body">
          <span class="package-card__name">
            {{ item.label }}
            <span v-if="item.recommended" class="package-card__badge">推荐</span>
          </span>
          <span class="package-card__desc">{{ item.description }}</span>
        </span>
        <span class="package-card__size">{{ item.size }}</span>
      </button>
    </div>

    <div class="download-actions">
      <el-button type="primary" :disabled="!canDownload" :loading="downloading" @click="downloadSoftware">
        {{ downloadButtonText }}
      </el-button>
      <el-button @click="closeDialog">关闭</el-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { confirmInstallPackageOnMobile, getInstallPackages, resolveDownloadUrl, resolvePlusFilename, triggerDownload } from '../js/download';
import { useIsMobile } from '../composables/useIsMobile';

const emit = defineEmits(['close']);
const { isMobile } = useIsMobile();
const packages = ref(getInstallPackages());
const selectedId = ref('standard');
const downloading = ref(false);

const selectedPackage = computed(() =>
  packages.value.find((item) => item.id === selectedId.value) || packages.value[0] || null
);

const canDownload = computed(() => Boolean(selectedPackage.value?.filename));

const downloadButtonText = computed(() => {
  if (!selectedPackage.value) return '下载安装包';
  if (isMobile.value) return `下载${selectedPackage.value.label}`;
  return `下载${selectedPackage.value.label}（${selectedPackage.value.size}）`;
});

onMounted(async () => {
  if (!packages.value.some((item) => item.id === selectedId.value)) {
    selectedId.value = packages.value[0]?.id || 'standard';
  }

  const plus = packages.value.find((item) => item.id === 'plus');
  const standard = packages.value.find((item) => item.id === 'standard');
  if (plus && !plus.filename) {
    const filename = await resolvePlusFilename(standard?.filename);
    if (filename) {
      packages.value = packages.value.map((item) => (
        item.id === 'plus' ? { ...item, filename } : item
      ));
    }
  }
});

const downloadSoftware = async () => {
  const item = selectedPackage.value;
  if (!item?.filename) {
    ElMessage.error('下载地址未配置');
    return;
  }

  downloading.value = true;
  try {
    if (!(await confirmInstallPackageOnMobile())) return;
    triggerDownload(resolveDownloadUrl(item.filename), item.filename);
    ElMessage.success(`${item.label}下载已开始（${item.size}）`);
    emit('close');
  } catch (error) {
    console.error('下载失败:', error);
    ElMessage.error(`下载失败: ${error.message}`);
  } finally {
    downloading.value = false;
  }
};

const closeDialog = () => {
  emit('close');
};
</script>

<style scoped>
.download-area {
  color: var(--dca-text);
}

.download-lead {
  margin: 0 0 16px;
  color: var(--dca-text-muted);
  font-size: 14px;
  line-height: 1.6;
}

.package-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.package-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  color: inherit;
  background: var(--dca-surface-2);
  border: 1px solid var(--dca-border);
  border-radius: var(--dca-radius);
  box-sizing: border-box;
}

.package-card.is-selected {
  border-color: var(--dca-accent);
  background: var(--dca-accent-soft);
}

.package-card__check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  border: 2px solid var(--dca-border-strong);
  border-radius: 50%;
  box-sizing: border-box;
}

.package-card.is-selected .package-card__check {
  border-color: var(--dca-accent);
  box-shadow: inset 0 0 0 4px var(--dca-accent);
}

.package-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.package-card__name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 15px;
  font-weight: var(--dca-fw-semibold);
}

.package-card__badge {
  padding: 1px 6px;
  font-size: 12px;
  font-weight: var(--dca-fw-medium);
  color: var(--dca-accent-ink);
  background: var(--dca-accent);
  border-radius: 999px;
}

.package-card__size {
  padding-top: 2px;
  font-size: 13px;
  color: var(--dca-accent);
  font-weight: var(--dca-fw-medium);
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.package-card__desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--dca-text-muted);
}

.download-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

@media (max-width: 768px) {
  .download-lead {
    margin-bottom: 12px;
    font-size: 13px;
    line-height: 1.55;
  }

  .package-list {
    gap: 8px;
  }

  .package-card {
    gap: 10px;
    padding: 12px;
  }

  .package-card__name {
    font-size: 14px;
  }

  .package-card__desc {
    font-size: 12px;
  }

  .download-actions {
    flex-direction: column-reverse;
    margin-top: 14px;
  }

  .download-actions .el-button {
    width: 100%;
    margin: 0;
  }
}
</style>
